/**
 * UNIFIED ERROR SERVICE - EKOSE-RX
 * 
 * Centralise toute la logique de gestion d'erreurs, résilience (Retry/Circuit Breaker)
 * et monitoring frontend.
 */

import { AxiosError } from 'axios';
import { logger } from '../utils/logger';

// ========================================
// 1. TYPES ET CLASSES D'ERREURS
// ========================================

export type ErrorSeverity = 'low' | 'medium' | 'high' | 'critical';
export type ErrorCategory = 'auth' | 'network' | 'validation' | 'timeout' | 'server' | 'unknown';

export interface AppErrorDetails {
  message?: string;
  code?: string;
  [key: string]: unknown;
}

export class AppError extends Error {
  constructor(
    public message: string,
    public code: string = 'UNKNOWN_ERROR',
    public statusCode: number = 500,
    public severity: ErrorSeverity = 'medium',
    public details?: AppErrorDetails
  ) {
    super(message);
    this.name = 'AppError';
  }
}

interface QueuedAppError {
  message: string;
  code: string;
  statusCode: number;
  severity: ErrorSeverity;
  details?: AppErrorDetails;
  context: string;
  timestamp: string;
}

// ========================================
// 2. MOTEUR DE RÉSILIENCE (Circuit Breaker & Retry)
// ========================================

class CircuitBreaker {
  private failures = 0;
  private state: 'closed' | 'open' | 'half-open' = 'closed';
  private lastFailureTime?: number;

  constructor(
    private threshold: number = 5,
    private timeout: number = 30000
  ) {}

  canProceed(): boolean {
    if (this.state === 'closed') return true;
    if (this.state === 'open' && Date.now() - (this.lastFailureTime || 0) > this.timeout) {
      this.state = 'half-open';
      return true;
    }
    return false;
  }

  recordSuccess() {
    this.failures = 0;
    this.state = 'closed';
  }

  recordFailure() {
    this.failures++;
    this.lastFailureTime = Date.now();
    if (this.failures >= this.threshold) {
      this.state = 'open';
      logger.error('⚠️ Circuit Breaker OPEN');
    }
  }
}

// ========================================
// 3. SERVICE UNIFIÉ
// ========================================

class UnifiedErrorService {
  private static instance: UnifiedErrorService;
  private breakers = new Map<string, CircuitBreaker>();
  private errorQueue: QueuedAppError[] = [];

  private constructor() {}

  static getInstance(): UnifiedErrorService {
    if (!this.instance) this.instance = new UnifiedErrorService();
    return this.instance;
  }

  /**
   * Analyse une erreur (Axios ou autre) et retourne une AppError standardisée
   */
  handleError(error: unknown, context: string = 'App'): AppError {
    let message = 'Une erreur est survenue';
    let code = 'UNKNOWN_ERROR';
    let status = 500;
    let severity: ErrorSeverity = 'medium';
    let details: AppErrorDetails | undefined;

    if (error instanceof AppError) return error;

    const axiosError = error as AxiosError<AppErrorDetails>;

    if (axiosError?.isAxiosError) {
      status = axiosError.response?.status || 0;
      const data = axiosError.response?.data;
      
      message = data?.message || axiosError.message || message;
      code = data?.code || (status === 0 ? 'NETWORK_ERROR' : `HTTP_${status}`);
      details = data;
      
      if (status === 401) severity = 'critical';
      else if (status >= 500) severity = 'high';
    } else if (error instanceof Error) {
      message = error.message;
      code = 'JS_ERROR';
    }

    const appError = new AppError(message, code, status, severity, details);
    
    // Logging et Monitoring
    this.reportError(appError, context);
    
    return appError;
  }

  /**
   * Exécute une fonction avec stratégie de Retry et Circuit Breaker
   */
  async withResilience<T>(
    fn: () => Promise<T>,
    key: string,
    options = { retries: 2, delay: 1000 }
  ): Promise<T> {
    const breaker = this.getBreaker(key);
    if (!breaker.canProceed()) {
      throw new AppError('Service temporairement indisponible (Circuit Breaker)', 'SERVICE_UNAVAILABLE', 503, 'high');
    }

    let lastErr: any;
    for (let i = 0; i <= options.retries; i++) {
      try {
        const result = await fn();
        breaker.recordSuccess();
        return result;
      } catch (err: any) {
        lastErr = err;
        const status = err.response?.status;
        
        // On ne réessaie pas les erreurs client (400, 401, 403, 404)
        if (status && status >= 400 && status < 500) break;
        
        if (i < options.retries) {
          const delay = options.delay * Math.pow(2, i);
          await new Promise(res => setTimeout(res, delay));
        }
      }
    }

    breaker.recordFailure();
    throw this.handleError(lastErr, `Resilience:${key}`);
  }

  private getBreaker(key: string): CircuitBreaker {
    if (!this.breakers.has(key)) this.breakers.set(key, new CircuitBreaker());
    return this.breakers.get(key)!;
  }

  private reportError(error: AppError, context: string) {
    logger.error(`[${context}] ${error.code}: ${error.message}`, { 
      severity: error.severity,
      details: error.details 
    });
    
    this.errorQueue.push({
      message: error.message,
      code: error.code,
      statusCode: error.statusCode,
      severity: error.severity,
      details: error.details,
      context,
      timestamp: new Date().toISOString(),
    });
    if (this.errorQueue.length > 50) this.errorQueue.shift();
    
    // TODO: Dispatch to Sentry if available in frontend
  }
}

export const errorService = UnifiedErrorService.getInstance();
export default errorService;
